import moment from "moment";
import { PillTone } from "../../Component/Pill";

// Row returned by GET /subscription/invoices (one cs_invoice entry)
export interface ApiInvoice {
  _id: string;
  invoice_id?: number;
  inovoice_order_id?: string;
  invoice_plan_name?: string;
  invoice_plan_type?: string;
  invoice_plan_duration?: number;
  invoice_plan_start_date?: string;
  invoice_plan_end_date?: string;
  invoice_status?: number;
  invoice_amount?: string | number;
  invoice_base_amount?: number;
  invoice_cgst_amount?: number;
  invoice_sgst_amount?: number;
  invoice_igst_amount?: number;
  invoice_custom_discount?: number;
  invoice_credit_applied?: number;
  invoice_type?: string;
  invoice_description?: string;
  invoice_bill_buisness_name?: string;
  invoice_bill_name?: string;
  invoice_bill_email?: string;
  invoice_bill_contact_no?: string;
  invoice_bill_gst_no?: string;
  invoice_bill_street_name?: string;
  invoice_bill_building_name?: string;
  invoice_bill_city?: string;
  invoice_bill_state?: string;
  invoice_bill_country?: string;
  invoice_bill_postal_code?: string;
  invoice_user_id?: { _id: string; user_unique_id?: string; user_companyName?: string; user_first_name?: string; user_last_name?: string; user_email?: string };
  invoice_transaction_id?: { _id: string; method?: string; status?: string; transaction_order_id?: string };
  createdAt?: string;
}

// invoice_plan_type → [label, icon]
const PLAN_TYPE: Record<string, [string, string]> = {
  wallet_topup: ["Wallet top-up", "ri-wallet-3-line"],
  wallet_deduction: ["Wallet deduction", "ri-indeterminate-circle-line"],
  monthly: ["Monthly plan", "ri-price-tag-3-line"],
  quarterly: ["Quarterly plan", "ri-price-tag-3-line"],
  yearly: ["Yearly plan", "ri-price-tag-3-line"],
  annually: ["Yearly plan", "ri-price-tag-3-line"],
  other: ["Other", "ri-file-list-3-line"],
};

export const planTypeInfo = (type?: string): [string, string] =>
  PLAN_TYPE[type || "other"] || [type ? type.replace(/_/g, " ").replace(/\b\w/g, x => x.toUpperCase()) : "Other", "ri-price-tag-3-line"];

export const isWalletInvoice = (i: ApiInvoice) => i.invoice_plan_type === "wallet_topup" || i.invoice_plan_type === "wallet_deduction";

// invoice_status 1 = paid, anything else = not paid yet
export const invoiceStatus = (i: ApiInvoice): [PillTone, string] => (i.invoice_status === 1 ? ["ok", "paid"] : ["warn", "unpaid"]);

export const invoiceNo = (i: ApiInvoice) => i.inovoice_order_id || (i.invoice_id ? `#${i.invoice_id}` : "—");

export const customerName = (i: ApiInvoice) =>
  i.invoice_user_id?.user_companyName || i.invoice_bill_buisness_name || i.invoice_bill_name || "—";

export const inr2 = (n: number) => "₹" + Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Short ₹ for the stats strip (₹27.4L)
export const inrShort = (n: number) => {
  if (n >= 10000000) return `₹${(n / 10000000).toFixed(1)}Cr`;
  if (n >= 100000) return `₹${(n / 100000).toFixed(1)}L`;
  return "₹" + Math.round(n || 0).toLocaleString("en-IN");
};

// Amounts as stored on the invoice; taxable value falls back to total − GST
export const invoiceTotals = (i: ApiInvoice) => {
  const tot = Number(i.invoice_amount || 0);
  const cgst = Number(i.invoice_cgst_amount || 0);
  const sgst = Number(i.invoice_sgst_amount || 0);
  const igst = Number(i.invoice_igst_amount || 0);
  const gst = cgst + sgst + igst;
  const sub = Number(i.invoice_base_amount || 0) || Math.max(0, tot - gst);
  return { sub, cgst, sgst, igst, gst, tot, intra: cgst > 0 || sgst > 0 };
};

// Line item text on the invoice
export const invoiceLine = (i: ApiInvoice) => {
  if (isWalletInvoice(i)) return i.invoice_plan_name || planTypeInfo(i.invoice_plan_type)[0];
  const range = i.invoice_plan_start_date && i.invoice_plan_end_date
    ? ` (${moment(i.invoice_plan_start_date).format("DD MMM YYYY")} – ${moment(i.invoice_plan_end_date).format("DD MMM YYYY")})`
    : "";
  return `${i.invoice_plan_name || "Plan"} — ${planTypeInfo(i.invoice_plan_type)[0]}${range}`;
};
